import { z } from 'zod';
import {
  WorkspaceId,
  UserId,
  ArtistId,
  ImprovProfileId,
} from '../ids.js';
import {
  Slug,
  PublicCode,
  IanaTimezone,
  CurrencyCode,
  Timestamps,
  Version,
  SafeLink,
  HexColor,
  HttpsUrl,
} from '../primitives.js';
import { Role, Permission, WorkspaceStatus, MemberStatus, ArtistStatus } from '../enums.js';

/** §27.1 Workspace — the tenant boundary. */
export const Workspace = Timestamps.extend({
  workspaceId: WorkspaceId,
  name: z.string().min(1).max(120),
  slug: Slug,
  status: WorkspaceStatus,
  defaultTimezone: IanaTimezone,
  defaultCurrency: CurrencyCode,
  planId: z.string().min(1),
  settings: z.record(z.string(), z.unknown()).default({}),
  version: Version,
});
export type Workspace = z.infer<typeof Workspace>;

/** §27.2 WorkspaceMember. `artistIds` empty means all artists in the workspace. */
export const WorkspaceMember = z.object({
  workspaceId: WorkspaceId,
  userId: UserId,
  role: Role,
  artistIds: z.array(ArtistId).default([]),
  permissions: z.array(Permission).default([]),
  status: MemberStatus,
  invitedAt: z.string().datetime({ offset: false }).nullable().default(null),
  acceptedAt: z.string().datetime({ offset: false }).nullable().default(null),
  lastActiveAt: z.string().datetime({ offset: false }).nullable().default(null),
  version: Version,
});
export type WorkspaceMember = z.infer<typeof WorkspaceMember>;

/** Constrained portable-text block (§16). No arbitrary HTML. */
export const RichTextBlock = z.object({
  type: z.enum(['paragraph', 'heading', 'list_item']),
  text: z.string().max(2000),
});
export type RichTextBlock = z.infer<typeof RichTextBlock>;

/** A labeled public link (§16). */
export const PublicLink = z.object({
  label: z.string().min(1).max(60),
  url: SafeLink,
});
export type PublicLink = z.infer<typeof PublicLink>;

/** Artist public profile (§16). Booking email is private unless explicitly published. */
export const ArtistProfile = z.object({
  publicName: z.string().min(1).max(120),
  pronunciation: z.string().max(120).optional(),
  tagline: z.string().max(160).optional(),
  shortBio: z.string().max(600).optional(),
  longBio: z.array(RichTextBlock).default([]),
  profileImageKey: z.string().optional(),
  logoKey: z.string().optional(),
  website: HttpsUrl.optional(),
  socialLinks: z.array(PublicLink).default([]),
  streamingLinks: z.array(PublicLink).default([]),
  tipJarCopy: z.string().max(280).optional(),
  merchandiseLink: HttpsUrl.optional(),
  bookingLink: SafeLink.optional(),
  bookingEmailPublished: z.boolean().default(false),
  altText: z.string().max(280).optional(),
});
export type ArtistProfile = z.infer<typeof ArtistProfile>;

/** Limited theme tokens (§16, §11.3 branding.customTheme). Contrast enforced in domain. */
export const ThemeTokens = z.object({
  background: HexColor.optional(),
  surface: HexColor.optional(),
  text: HexColor.optional(),
  accent: HexColor.optional(),
  mode: z.enum(['light', 'dark', 'system']).default('system'),
});
export type ThemeTokens = z.infer<typeof ThemeTokens>;

/** §27.3 Artist. */
export const Artist = Timestamps.extend({
  artistId: ArtistId,
  workspaceId: WorkspaceId,
  name: z.string().min(1).max(120),
  slug: Slug,
  status: ArtistStatus,
  profile: ArtistProfile,
  theme: ThemeTokens.default({ mode: 'system' }),
  defaultFeatureOverrides: z.record(z.string(), z.unknown()).default({}),
  defaultImprovProfileId: ImprovProfileId.nullable().default(null),
  stablePublicCode: PublicCode,
  version: Version,
});
export type Artist = z.infer<typeof Artist>;
